import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { userAPI, levelAPI, organizationRoleAPI, departmentAPI } from '../../api/services';
import { User } from '../../types';

interface Level {
  _id: string;
  name: string;
  code: string;
  rank: number;
  description?: string;
}

interface OrganizationRole {
  _id: string;
  name: string;
  code: string;
  priority: number;
  description?: string;
}

interface Department {
  _id: string;
  name: string;
  code: string;
}

const EditUser: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [name, setName] = useState('');
  const [levelId, setLevelId] = useState('');
  const [organizationRoleId, setOrganizationRoleId] = useState('');
  const [primaryDepartmentId, setPrimaryDepartmentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Dynamic data
  const [levels, setLevels] = useState<Level[]>([]);
  const [roles, setRoles] = useState<OrganizationRole[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (id) {
      fetchUser(id);
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setDataLoading(true);
      const [levelsRes, rolesRes, deptsRes] = await Promise.all([
        levelAPI.getAllLevels(),
        organizationRoleAPI.getAllRoles(),
        departmentAPI.getAllDepartments(),
      ]);

      setLevels(levelsRes.data.data || []);
      setRoles(rolesRes.data.data || []);
      setDepartments(deptsRes.data.data || []);
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load form data. Please refresh the page.');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchUser = async (userId: string) => {
    try {
      const response = await userAPI.getUserById(userId);
      const userData = response.data;
      setUser(userData);
      setName(userData.name || '');

      // Set levelId from user data
      if (userData.levelId) {
        setLevelId(typeof userData.levelId === 'object' ? userData.levelId._id : userData.levelId);
      }

      // Set organizationRoleId from user data
      if (userData.organizationRoleId) {
        setOrganizationRoleId(
          typeof userData.organizationRoleId === 'object'
            ? userData.organizationRoleId._id
            : userData.organizationRoleId
        );
      }

      // Set primaryDepartmentId from user data
      if (userData.primaryDepartmentId) {
        setPrimaryDepartmentId(
          typeof userData.primaryDepartmentId === 'object'
            ? userData.primaryDepartmentId._id
            : userData.primaryDepartmentId
        );
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch user');
    }
  };

  const parseApiError = (msg: string): string => {
    if (!msg) return msg;
    if (msg.includes('E11000') || msg.toLowerCase().includes('duplicate key')) {
      return 'This email is already registered.';
    }
    return msg;
  };

  const validateEdit = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required.';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters.';
    }
    if (!levelId) {
      newErrors.levelId = 'Level is required.';
    }
    if (!organizationRoleId) {
      newErrors.organizationRoleId = 'Organization role is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    if (!validateEdit()) return;

    setLoading(true);
    setError('');

    try {
      await userAPI.updateUser(id, {
        name,
        levelId,
        organizationRoleId,
        primaryDepartmentId: primaryDepartmentId || null,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);
    } catch (err: any) {
      const raw = err.response?.data?.message || 'Failed to update user';
      setError(parseApiError(raw));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    if (!window.confirm(`Are you sure you want to delete ${user?.name}?`)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await userAPI.deleteUser(id);
      navigate('/admin/users');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
      setLoading(false);
    }
  };

  if (dataLoading || !user) {
    return (
      <div className="min-h-screen bg-gray-100 py-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Edit User</h2>
            <button
              onClick={() => navigate('/admin/users')}
              className="text-gray-600 hover:text-gray-800"
            >
              Back to Users
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                User updated successfully! Redirecting...
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors((prev) => { const n = { ...prev }; delete n.name; return n; }); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-2">
                Level *
              </label>
              <select
                id="level"
                value={levelId}
                onChange={(e) => { setLevelId(e.target.value); setErrors((prev) => { const n = { ...prev }; delete n.levelId; return n; }); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.levelId ? 'border-red-400' : 'border-gray-300'}`}
              >
                <option value="">Select Level</option>
                {levels.map((level) => (
                  <option key={level._id} value={level._id}>
                    {level.code} - {level.name}
                    {level.description && ` (${level.description})`}
                  </option>
                ))}
              </select>
              {errors.levelId && <p className="mt-1 text-xs text-red-600">{errors.levelId}</p>}
              <p className="text-xs text-gray-500 mt-1">
                Changing the level will change the evaluation form the user sees
              </p>
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                Organization Role *
              </label>
              <select
                id="role"
                value={organizationRoleId}
                onChange={(e) => { setOrganizationRoleId(e.target.value); setErrors((prev) => { const n = { ...prev }; delete n.organizationRoleId; return n; }); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.organizationRoleId ? 'border-red-400' : 'border-gray-300'}`}
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name} (Priority: {role.priority})
                  </option>
                ))}
              </select>
              {errors.organizationRoleId && <p className="mt-1 text-xs text-red-600">{errors.organizationRoleId}</p>}
              <p className="text-xs text-gray-500 mt-1">
                Higher priority roles have more permissions (e.g., HOD: 50, Employee: 10)
              </p>
            </div>

            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                Primary Department
              </label>
              <select
                id="department"
                value={primaryDepartmentId}
                onChange={(e) => setPrimaryDepartmentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Department (Optional)</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name} ({dept.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading || success || !levelId || !organizationRoleId}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition duration-200"
              >
                {loading ? 'Updating...' : 'Update User'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading || success}
                className="bg-red-600 text-white py-2 px-6 rounded-md hover:bg-red-700 disabled:bg-red-300 transition duration-200"
              >
                Delete User
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
